import { APP_MANAGED_ROUTES } from "common/app-managed-routes";
import { useEffect } from "react";
import { useNavigate } from "@leagueplatform/routing";
import { useAuth } from "@leagueplatform/auth-utils";
import { LoadingIndicator } from "@leagueplatform/web-common-components";
import {
  queryHelpers,
  StackLayout,
  useMediaQuery,
  Image,
  HeadingText,
  Button,
} from "@leagueplatform/genesis-core";
import LogoImage from "assets/quest/transform-your-health-logo.svg";
import { useIntl } from "@leagueplatform/locales";
import { GenericError } from "components/generic-error/generic-error";
import { QueryParamModal } from "components/query-param-modal/query-param-modal.component";
import { SignInFooter } from "./sign-in-footer.component";
import { PreLoginPageBackgroundImage } from "./pre-login-image-section.component";

interface ContentProps {
  isMobile: boolean;
  onSignIn: () => void;
  onCreateAccount: () => void;
  signInText: string;
  createAccountText: string;
}

const Content = ({
  isMobile,
  onSignIn,
  onCreateAccount,
  signInText,
  createAccountText,
}: ContentProps) => (
  <>
    <PreLoginPageBackgroundImage isMobile={isMobile} />
    <StackLayout
      css={{
        alignSelf: "center",
        justifyContent: "center",
        flexBasis: "55%",
        width: "100%",
        height: "fit-content",
      }}
    >
      <StackLayout
        orientation="vertical"
        horizontalAlignment="center"
        verticalAlignment="center"
        css={{
          width: "100%",
          padding: "$half",
          gap: "$three",
          marginBlockStart: "$two",
        }}
      >
        <HeadingText size="xl" level="1" css={{ flexGrow: 0 }}>
          <Image src={LogoImage} width={249} height={112} />
        </HeadingText>
        <StackLayout css={{ gap: "$one", flexGrow: 0 }}>
          <Button
            size="large"
            priority="primary"
            width="fillContainer"
            css={{ marginBottom: "$half", width: "327px" }}
            onClick={onSignIn}
          >
            {signInText}
          </Button>
          <Button
            size="large"
            priority="secondary"
            width="fillContainer"
            css={{ marginBottom: "$half", width: "327px" }}
            onClick={onCreateAccount}
          >
            {createAccountText}
          </Button>
        </StackLayout>
        <SignInFooter />
      </StackLayout>
    </StackLayout>
  </>
);

export const PreLoginLandingPage = () => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.between([0, "tablet"]));
  const { isAuthenticated, appState, error } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate(appState?.redirectPath || APP_MANAGED_ROUTES.HOME);
    }
  }, [isAuthenticated, appState, navigate]);

  if (error) {
    return (
      <GenericError
        bodyString={formatMessage({
          id: "STR_ERROR_STATE_BODY_LOGIN",
        })}
      />
    );
  }

  const renderContent = () => (
    <Content
      isMobile={isMobile}
      onSignIn={() => navigate(APP_MANAGED_ROUTES.SIGN_IN)}
      onCreateAccount={() => console.log("Create Account Clicked")} // TODO: replace with proper navigation
      signInText={formatMessage({ id: "STR_LOG_IN" })}
      createAccountText={formatMessage({ id: "STR_CREATE_ACCOUNT" })}
    />
  );

  return (
    <>
      <QueryParamModal isPublic />
      <StackLayout
        verticalAlignment="stretch"
        horizontalAlignment="stretch"
        orientation={{
          "@initial": "horizontal",
          "@mobile": "vertical",
          "@mobileLandscape": "vertical",
        }}
        css={{ minWidth: "100vw", minHeight: "100vh", height: "100vh" }}
      >
        {isAuthenticated === undefined && <LoadingIndicator />}
        {isAuthenticated !== undefined && renderContent()}
      </StackLayout>
    </>
  );
};
